/**
 * Track Signup Page — הרשמה לאזור פתוח
 *
 * The whole sign-up for תכנון פרישה / משכנתא: name, email, password, consent.
 *
 * Deliberately NOT a parameterized StudentSignupPage — the class-code preview
 * and parental-consent handling are about 40% of that file and irrelevant here.
 * And deliberately shorter than the client registration: these areas are free
 * explanatory content, so no national ID, no date of birth, no phone. Data we
 * would not use is a liability, not a feature.
 */

import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { AlertCircle, CheckCircle, Eye, EyeOff, LogIn } from 'lucide-react'
import PasswordStrengthIndicator from '../../components/auth/PasswordStrengthIndicator'
import api, { handleApiError } from '../../services/api'
import { TRACKS, MAZAN, type TrackKey } from '../../config/tracks'

const trackSchema = z
  .object({
    firstName: z.string().min(2, 'שם פרטי חייב להכיל לפחות 2 תווים').max(50, 'שם פרטי ארוך מדי'),
    lastName: z.string().min(2, 'שם משפחה חייב להכיל לפחות 2 תווים').max(50, 'שם משפחה ארוך מדי'),
    email: z.string().email('כתובת אימייל לא תקינה').min(1, 'שדה חובה'),
    password: z
      .string()
      .min(8, 'הסיסמה חייבת להכיל לפחות 8 תווים')
      .regex(/[A-Za-z֐-׿]/, 'הסיסמה חייבת להכיל לפחות אות אחת')
      .regex(/[0-9]/, 'הסיסמה חייבת להכיל לפחות ספרה אחת'),
    confirmPassword: z.string().min(1, 'שדה חובה'),
    marketingConsent: z.boolean().optional(),
    privacyPolicyAccepted: z.boolean().refine((v) => v === true, {
      message: 'חובה לקבל את תנאי השימוש ומדיניות הפרטיות',
    }),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'הסיסמאות אינן תואמות',
    path: ['confirmPassword'],
  })

type TrackFormData = z.infer<typeof trackSchema>

interface TrackSignupPageProps {
  /** a free track, or 'mazan' — the paid one-time report (same light account) */
  track: TrackKey | typeof MAZAN.key
}

/** The copy that differs between the free areas and the paid מאזן report. */
function signupCopy(track: TrackSignupPageProps['track']) {
  if (track === MAZAN.key) {
    return {
      name: MAZAN.name,
      tagline: MAZAN.tagline,
      Icon: MAZAN.Icon,
      basePath: MAZAN.homePath,
      note: 'חשבון אישי לשמירת הנתונים, לתשלום ולהורדת הדוח. התשלום רק בסוף, ומיד אחריו מוצגות התוצאות.',
      submit: 'יצירת חשבון',
      footer: 'הדוח מבוסס על הנתונים שמוזנים בו ומהווה טיוטה לפני הערות הצדדים.',
    }
  }
  const t = TRACKS[track]
  return {
    name: t.name,
    tagline: t.tagline,
    Icon: t.Icon,
    basePath: t.basePath,
    note: 'חינם, בלי התחייבות, ובלי צורך בחתימה על הסכם ייעוץ.',
    submit: 'הרשמה חינם',
    footer: 'האזור מספק מידע כללי בלבד ואינו מהווה ייעוץ אישי.',
  }
}

export default function TrackSignupPage({ track }: TrackSignupPageProps) {
  const config = signupCopy(track)
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [alreadyRegistered, setAlreadyRegistered] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<TrackFormData>({
    resolver: zodResolver(trackSchema),
    defaultValues: { privacyPolicyAccepted: false, marketingConsent: false },
  })

  const password = watch('password')

  const onSubmit = async (data: TrackFormData) => {
    setIsLoading(true)
    setError(null)
    setAlreadyRegistered(null)
    try {
      const response = await api.post('/auth/register-track', {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        confirmPassword: data.confirmPassword,
        track,
        marketingConsent: data.marketingConsent || false,
        privacyPolicyAccepted: data.privacyPolicyAccepted,
      })

      // One account covers every open area, so an existing address is a sign-in
      // prompt rather than an error.
      if (response.data?.alreadyRegistered) {
        setAlreadyRegistered(response.data.message)
        return
      }
      setSuccess(true)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-bl from-primary-50 to-white p-4">
        <div className="w-full max-w-md">
          <div className="card text-center">
            <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle size={32} className="text-green-600" aria-hidden="true" />
            </div>
            <h1 className="mb-2 font-heading text-2xl font-bold text-gray-900">ההרשמה הושלמה!</h1>
            <p className="mb-2 text-gray-600">שלחנו אליך אימייל לאימות כתובת הדוא"ל.</p>
            <p className="mb-4 text-gray-600">
              אנא בדוק/י את תיבת הדואר (כולל ספאם) ולחצ/י על קישור האימות.
            </p>
            <p className="text-sm text-gray-500">
              לאחר האימות אפשר להתחבר ולהיכנס לאזור {config.name}.
            </p>
            <Link to="/login" className="btn btn-primary mt-6 inline-flex">
              למסך ההתחברות
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-bl from-primary-50 to-white p-4">
      <div className="w-full max-w-md">
        <div className="card">
          <header className="mb-6 text-center">
            <div className="mb-3 inline-flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <config.Icon size={28} aria-hidden="true" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-gray-900">
              הרשמה — {config.name}
            </h1>
            <p className="mt-1 text-sm text-gray-600">{config.tagline}</p>
            <p className="mt-2 text-xs text-gray-500">{config.note}</p>
          </header>

          {alreadyRegistered && (
            <div
              className="mb-4 flex items-start gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-900"
              role="status"
            >
              <LogIn size={16} aria-hidden="true" className="mt-0.5 flex-shrink-0" />
              <div>
                <p>{alreadyRegistered}</p>
                <button
                  type="button"
                  onClick={() => navigate(`/login?next=${config.basePath}`)}
                  className="mt-2 font-medium underline"
                >
                  למסך ההתחברות
                </button>
              </div>
            </div>
          )}

          {error && (
            <div
              className="mb-4 flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-800"
              role="alert"
            >
              <AlertCircle size={16} aria-hidden="true" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="firstName" className="mb-1 block text-sm font-medium text-gray-700">
                  שם פרטי
                </label>
                <input id="firstName" type="text" className="input" {...register('firstName')} />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-700">{errors.firstName.message}</p>
                )}
              </div>
              <div>
                <label htmlFor="lastName" className="mb-1 block text-sm font-medium text-gray-700">
                  שם משפחה
                </label>
                <input id="lastName" type="text" className="input" {...register('lastName')} />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-700">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
                אימייל
              </label>
              <input
                id="email"
                type="email"
                dir="ltr"
                autoComplete="email"
                className="input"
                {...register('email')}
              />
              {errors.email && <p className="mt-1 text-sm text-red-700">{errors.email.message}</p>}
            </div>

            <div className="mt-4">
              <label htmlFor="password" className="mb-1 block text-sm font-medium text-gray-700">
                סיסמה
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  dir="ltr"
                  autoComplete="new-password"
                  className="input pe-10"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 end-2 flex items-center text-gray-500"
                  aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-700">{errors.password.message}</p>
              )}
              <PasswordStrengthIndicator password={password || ''} />
            </div>

            <div className="mt-4">
              <label
                htmlFor="confirmPassword"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                אימות סיסמה
              </label>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                dir="ltr"
                autoComplete="new-password"
                className="input"
                {...register('confirmPassword')}
              />
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-700">{errors.confirmPassword.message}</p>
              )}
            </div>

            <div className="mt-5 space-y-3">
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input type="checkbox" className="mt-1" {...register('privacyPolicyAccepted')} />
                <span>
                  קראתי ואני מסכים/ה ל
                  <Link to="/terms" className="text-primary-700 underline" target="_blank">
                    תנאי השימוש
                  </Link>{' '}
                  ול
                  <Link to="/privacy" className="text-primary-700 underline" target="_blank">
                    מדיניות הפרטיות
                  </Link>
                </span>
              </label>
              {errors.privacyPolicyAccepted && (
                <p className="text-sm text-red-700">{errors.privacyPolicyAccepted.message}</p>
              )}

              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input type="checkbox" className="mt-1" {...register('marketingConsent')} />
                <span>אשמח לקבל עדכונים ותכנים במייל (לא חובה)</span>
              </label>
            </div>

            <button type="submit" disabled={isLoading} className="btn btn-primary mt-6 w-full">
              {isLoading ? 'נרשם...' : config.submit}
            </button>
          </form>

          <div className="mt-6 border-t border-gray-200 pt-4 text-center text-sm text-gray-600">
            <p>
              כבר יש לך חשבון?{' '}
              <Link to={`/login?next=${config.basePath}`} className="text-primary-700 underline">
                התחברות
              </Link>
            </p>
            <p className="mt-2">
              <Link to="/" className="text-gray-500 underline">
                חזרה לעמוד הבית
              </Link>
            </p>
          </div>
        </div>

        <p className="mt-4 text-center text-xs text-gray-500">{config.footer}</p>
      </div>
    </div>
  )
}
